import { ConstraintRule } from '../constraint-rule';
import { BookingParams, BookingPosition, ConstraintSummary, StockSourceType, } from '../../types';

/**
 * Prüft, ob eine Buchung in eine Negativmenge am Quelllager resultiert und dies nicht erlaubt ist
 */
export class RuleOverbookSourceStock extends ConstraintRule {
  /**
   * Greift nur, wenn negative Positionsmengen im System verboten sind.
   * @param position Zu prüfende Buchungsposition (nicht verwendet).
   * @param params Buchungsparameter, aus deren Systemzustand die Negativmengen-Erlaubnis gelesen wird.
   * @returns True, wenn negative Positionsmengen nicht erlaubt sind.
   */
  public async needsCheck(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<boolean> {
    // Regel greift nur, wenn negative Positionen verboten sind
    const { system } = params.context;
    return !system.allowNegativePositionAmounts;
  }

  /**
   * Wählt anhand des Quelltyps (Bufferstock, Maschine oder Fahrzeug) die passende Bestandsprüfung.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit Kontext und Adaptern.
   * @returns Erfüllt oder nicht erfüllt je nach verfügbarem Quellbestand.
   */
  public async check(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<ConstraintSummary> {
    const { booking } = params.context;
    switch (booking.sourceType) {
      case StockSourceType.BUFFERSTOCK:
        return this.checkSourceBufferstock(position, params);
      case StockSourceType.MACHINE:
        return this.checkSourceMachine(position, params);
      case StockSourceType.CAR:
        return this.checkSourceCar(position, params);
    }
  }

  /**
   * Prüft die lokal bekannte Bestandsmenge am Quell-Bufferstock gegen die Buchungsmenge.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit Kontext (Quell-Id) und Bufferstock-Adapter.
   * @returns Nicht erfüllt, wenn die Buchungsmenge den verfügbaren Bestand übersteigt, sonst erfüllt.
   */
  private async checkSourceBufferstock(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<ConstraintSummary> {
    const { context, adapters } = params;
    const { booking } = context;
    const available = await adapters.bufferstock.getArticleAmountLocal(
      booking.sourceId,
      position.article_id,
    );
    if (available >= position.amount) {
      return this.satisfied();
    }
    return this.unsatisfied(
      $localize`Die Buchungsmenge (${position.amount}) des Artikels ist größer als der Lagerbestand (${available})`,
    );
  }

  /**
   * Prüft die lokal bekannte Bestandsmenge im Quell-Maschinenlager gegen die Buchungsmenge.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit Kontext (Quell-Id) und Maschinen-Adapter.
   * @returns Nicht erfüllt, wenn die Buchungsmenge den verfügbaren Bestand übersteigt, sonst erfüllt.
   */
  private async checkSourceMachine(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<ConstraintSummary> {
    const { context, adapters } = params;
    const available = await adapters.machine.getArticleAmountLocal(
      context.booking.sourceId,
      position.article_id,
    );
    if (available >= position.amount) {
      return this.satisfied();
    }
    return this.unsatisfied(
      $localize`Die Buchungsmenge (${position.amount}) des Artikels ist größer als der Lagerbestand (${available})`,
    );
  }

  /**
   * Prüft die lokal bekannte Bestandsmenge im Quell-Fahrzeuglager gegen die Buchungsmenge.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit dem Fahrzeug-Adapter.
   * @returns Nicht erfüllt, wenn die Buchungsmenge den verfügbaren Bestand übersteigt, sonst erfüllt.
   */
  private async checkSourceCar(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<ConstraintSummary> {
    // CarAdapter.getArticleAmountLocal ist synchron (localStorage). Kein
    // Inventar ergibt 0 und wird damit wie eine Überbuchung behandelt.
    const available = params.adapters.car.getArticleAmountLocal(
      position.article_id,
    );
    if (available >= position.amount) {
      return this.satisfied();
    }
    return this.unsatisfied(
      $localize`Die Buchungsmenge (${position.amount}) des Artikels ist größer als der Lagerbestand (${available})`,
    );
  }
}
